import { fork, put, all, call, takeLatest } from "redux-saga/effects";

import {
  getSchedulesSuccess,
  getSchedulesFailure,
  getFrameworksSuccess,
  getFrameworksFailure,
  getControlsSuccess,
  getControlsFailure,
  getChecksSuccess,
  getChecksFailure,
  getSuppressionsSuccess,
  getSuppressionsFailure,
  bulkUpsertSuppressionsSuccess,
  bulkUpsertSuppressionsFailure,
} from "./suppressionSlice";

import {
  getSchedules as getSchedulesApi,
  getFrameworks as getFrameworksApi,
  getControls as getControlsApi,
  getChecks as getChecksApi,
  getSuppressions as getSuppressionsApi,
  bulkUpsertSuppressions as bulkUpsertSuppressionsApi,
} from "../../helpers/auth_aws_helper";

// 🛠️ Utility function to handle error responses consistently
const handleApiError = (error, operation) => {
  console.error(`Saga: ${operation} Error =`, error);
  
  if (error.response) {
    // Server responded with error status
    const errorData = error.response.data;
    
    // Handle API Gateway specific errors
    if (errorData?.message) {
      // API Gateway timeout or other gateway errors
      if (errorData.message.includes("timeout") || errorData.message.includes("timed out")) {
        return "Request timed out. Please try again.";
      }
      if (errorData.message.includes("Endpoint request")) {
        return "Service temporarily unavailable. Please try again later.";
      }
      return errorData.message;
    }
    
    // Handle other error formats
    if (typeof errorData === "string") {
      return errorData;
    }
    if (errorData?.error) {
      return errorData.error;
    }
    
    return `Server error: ${error.response.status}`;
  } else if (error.request) {
    // Request was made but no response received (timeout, network error, etc.)
    if (error.message && error.message.includes("timeout")) {
      return "Request timed out. Please check your connection and try again.";
    }
    return "No response received from server. Please check your connection.";
  } else {
    // Something else happened
    return error.message || "An unexpected error occurred.";
  }
};

// 🔄 Worker: Fetch Schedules
function* fetchSchedules({ payload: params }) {
  try {
    console.log("Saga: Calling getSchedules with params =", params);
    const response = yield call(getSchedulesApi, params);
    console.log("Saga: Schedules Response =", response);

    if (response.status === 200) {
      console.log("Saga: Full response object =", response);
      console.log("Saga: response.data =", response.data);
      console.log("Saga: response.status =", response.status);
      
      // The response structure is { ...response.data, status: response.status }
      // Remove status from response to get the actual data
      const { status, ...schedulesData } = response;
      console.log("Saga: Schedules Data =", schedulesData);
      
      yield put(getSchedulesSuccess(schedulesData));
    } else {
      // Handle non-200 status codes
      const errorMessage = response.data?.message || response.data || `Unexpected response: ${response.status}`;
      yield put(getSchedulesFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Fetch Schedules");
    yield put(getSchedulesFailure(errorMessage));
  }
}

// 🔄 Worker: Fetch Frameworks
function* fetchFrameworks({ payload: params }) {
  try {
    console.log("Saga: Calling getFrameworks with params =", params);
    const response = yield call(getFrameworksApi, params);
    console.log("Saga: Frameworks Response =", response);

    if (response.status === 200) {
      console.log("Saga: Full response object =", response);
      console.log("Saga: response.data =", response.data);
      console.log("Saga: response.status =", response.status);
      
      // The response structure is { ...response.data, status: response.status }
      // Remove status from response to get the actual data
      const { status, ...frameworksData } = response;
      console.log("Saga: Frameworks Data =", frameworksData);
      
      yield put(getFrameworksSuccess(frameworksData));
    } else {
      // Handle non-200 status codes
      const errorMessage = response.data?.message || response.data || `Unexpected response: ${response.status}`;
      yield put(getFrameworksFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Fetch Frameworks");
    yield put(getFrameworksFailure(errorMessage));
  }
}

// 🔄 Worker: Fetch Controls
function* fetchControls({ payload: { complianceaddon_id, params } }) {
  try {
    console.log("Saga: Calling getControls with complianceaddon_id =", complianceaddon_id, "params =", params);
    const response = yield call(getControlsApi, complianceaddon_id, params);
    console.log("Saga: Controls Response =", response);

    if (response.status === 200) {
      console.log("Saga: Full response object =", response);
      console.log("Saga: response.data =", response.data);
      console.log("Saga: response.status =", response.status);
      
      // The response structure is { ...response.data, status: response.status }
      // Remove status from response to get the actual data
      const { status, ...controlsData } = response;
      console.log("Saga: Controls Data =", controlsData);
      
      yield put(getControlsSuccess(controlsData));
    } else {
      // Handle non-200 status codes
      const errorMessage = response.data?.message || response.data || `Unexpected response: ${response.status}`;
      yield put(getControlsFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Fetch Controls");
    yield put(getControlsFailure(errorMessage));
  }
}

// 🔄 Worker: Fetch Checks
function* fetchChecks({ payload: { control_id, params } }) {
  try {
    console.log("Saga: Calling getChecks with control_id =", control_id, "params =", params);
    const response = yield call(getChecksApi, control_id, params);
    console.log("Saga: Checks Response =", response);

    if (response.status === 200) {
      console.log("Saga: Full response object =", response);
      console.log("Saga: response.data =", response.data);
      console.log("Saga: response.status =", response.status);
      
      // The response structure is { ...response.data, status: response.status }
      // Remove status from response to get the actual data
      const { status, ...checksData } = response;
      console.log("Saga: Checks Data =", checksData);
      
      yield put(getChecksSuccess(checksData));
    } else {
      // Handle non-200 status codes
      const errorMessage = response.data?.message || response.data || `Unexpected response: ${response.status}`;
      yield put(getChecksFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Fetch Checks");
    yield put(getChecksFailure(errorMessage));
  }
}

// 🔄 Worker: Fetch Suppressions
function* fetchSuppressions({ payload: { schedule_id, params } }) {
  try {
    console.log("Saga: Calling getSuppressions with schedule_id =", schedule_id, "params =", params);
    const response = yield call(getSuppressionsApi, schedule_id, params);
    console.log("Saga: Suppressions Response =", response);

    if (response.status === 200) {
      console.log("Saga: Full response object =", response);
      console.log("Saga: response.data =", response.data);
      console.log("Saga: response.status =", response.status);
      
      // The response structure is { ...response.data, status: response.status }
      // Remove status from response to get the actual data
      const { status, ...suppressionsData } = response;
      console.log("Saga: Suppressions Data =", suppressionsData);
      
      yield put(getSuppressionsSuccess(suppressionsData));
    } else {
      // Handle non-200 status codes
      const errorMessage = response.data?.message || response.data || `Unexpected response: ${response.status}`;
      yield put(getSuppressionsFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Fetch Suppressions");
    yield put(getSuppressionsFailure(errorMessage));
  }
}

// 🔄 Worker: Bulk Upsert Suppressions
function* bulkUpsertSuppressionsSaga({ payload: { schedule_id, payload, queryParams } }) {
  try {
    console.log("Saga: Calling bulkUpsertSuppressions with schedule_id =", schedule_id, "payload =", payload, "queryParams =", queryParams);
    const response = yield call(bulkUpsertSuppressionsApi, schedule_id, payload, queryParams);
    console.log("Saga: Bulk Suppressions Response =", response);

    if (response.status === 200) {
      console.log("Saga: Full response object =", response);
      console.log("Saga: response.data =", response.data);
      console.log("Saga: response.status =", response.status);
      
      const { status, ...suppressionsData } = response;
      console.log("Saga: Bulk Suppressions Data =", suppressionsData);
      
      yield put(bulkUpsertSuppressionsSuccess(suppressionsData));
    } else {
      const errorMessage = response.data?.message || response.data || `Unexpected response: ${response.status}`;
      yield put(bulkUpsertSuppressionsFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Bulk Upsert Suppressions");
    yield put(bulkUpsertSuppressionsFailure(errorMessage));
  }
}

// 👂 Watchers
function* watchSuppression() {
  yield takeLatest("suppression/getSchedules", fetchSchedules);
  yield takeLatest("suppression/getFrameworks", fetchFrameworks);
  yield takeLatest("suppression/getControls", fetchControls);
  yield takeLatest("suppression/getChecks", fetchChecks);
  yield takeLatest("suppression/getSuppressions", fetchSuppressions);
  yield takeLatest("suppression/bulkUpsertSuppressions", bulkUpsertSuppressionsSaga);
}

// 🧠 Root Saga
export function* suppressionSaga() {
  yield all([fork(watchSuppression)]);
}

export default suppressionSaga;

